import { useEffect, useRef } from 'react';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

/**
 * Cloudflare Turnstile (anti-bot).
 * - If no site key is configured, the component renders nothing
 *   (verification is also disabled on the backend in that case).
 * - onVerify(token) is called when the challenge is solved;
 *   onVerify('') when the token expires / on error.
 */
export default function TurnstileWidget({ onVerify }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!SITE_KEY) return undefined;

    let cancelled = false;

    const render = () => {
      if (cancelled || !window.turnstile || !containerRef.current) return;
      // Évite un double rendu (StrictMode / re-render)
      if (widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => onVerify(token),
        'expired-callback': () => onVerify(''),
        'error-callback': () => onVerify(''),
      });
    };

    // Load the script only once
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing && window.turnstile) {
      render();
    } else if (existing) {
      existing.addEventListener('load', render);
    } else {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', render);
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [onVerify]);

  if (!SITE_KEY) return null;

  return <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center' }} />;
}

// Lets pages know whether Turnstile is active (to require the token).
export const TURNSTILE_ENABLED = Boolean(SITE_KEY);
